import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent, ListTableComponent, PopupDialogComponent } from '@ppl/ui';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { STATIC_DATA } from '../../../../shared/constants/static-data.constants';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { ReferralDataService } from '../../../../services/referral-data.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { RelationshipInformation } from '../../../../interfaces/referralSections.interface';
import { ParentGuardianComponent, buildParentGuardianRow } from '../../../leads/participant-information/parent-guardian/parent-guardian.component';
import { mapParentGuardiansForSave, validateParentGuardians, parentRecordHasData } from '../../../leads/participant-information/parent-guardian/parent-guardian.util';

// Lead-details "Parent/Guardian Information" section (minor participants). Read view is a table; Edit
// opens a dialog hosting the shared ppl-parent-guardian form (intake rules: 2 base rows, NA on the
// second, up to 4). Save posts { participantId, programCode, relationshipInformation } to
// SAVE_PARTICIPANT_RELATIONSHIP_DETAILS and re-fetches.
@Component({
  selector: 'ppl-lead-detail-parent-guardian',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ButtonComponent, ListTableComponent, PopupDialogComponent, ParentGuardianComponent],
  templateUrl: './lead-detail-parent-guardian.component.html',
  styleUrl: './lead-detail-parent-guardian.component.css',
})
export class LeadDetailParentGuardianComponent extends BaseComponent implements OnInit {
  showDialog = false;
  parents!: FormArray;
  parentAddressErrors: Record<number, Record<string, string>> = {};
  relationshipOptions: any[] = [];
  leadStatus = '';
  approvedStatus = STATIC_DATA.APPROVED;
  tableData: any[] = [];
  private filledInfo: any;
  /** Set between a successful save and the lead refresh it triggers, so only that refresh rebuilds. */
  private awaitingSavedIds = false;

  cols = [
    { field: 'fullName', header: 'Name', jsonpath: 'fullName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'relationToParticipant', header: 'Relationship', jsonpath: 'relationToParticipant', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'dob', header: 'Date of Birth', jsonpath: 'dob', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'address', header: 'Address', jsonpath: 'address', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode = '';
  @Input() entry!: string;
  @Input() selectedLead!: number;
  @Input() readOnly = false;
  @Input() sectionData: any;

  @Output() leadUpdated = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private leadDetailsService: LeadDetailsService,
    private referralDataService: ReferralDataService,
    private dateTimeFormatterService: DateTimeFormatterService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.relationshipOptions = this.lookupService.getLookupValue('Relationship');
    this.parents = this.buildParents();

    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;
    });

    this.leadDetailsService.getUpdatedLeadData().subscribe((data) => {
      this.filledInfo = data?.responsedata?.infoData;
      this.buildTableData(this.filledInfo?.relationshipInformation || []);

      // Rows are sent with id:0 for new records (id>0 means update). If the dialog is still open
      // after a save, rebuild it from the refreshed records so the now-persisted ids replace the
      // zeros — otherwise saving a second time creates a duplicate set of parents. Guarded by
      // awaitingSavedIds so an unrelated lead refresh never discards in-progress edits.
      if (this.awaitingSavedIds) {
        this.awaitingSavedIds = false;
        if (this.showDialog) {
          this.parents = this.buildParents(this.filledInfo?.relationshipInformation || []);
        }
      }
    });
  }

  get canEdit(): boolean {
    return this.leadStatus !== this.approvedStatus && !this.readOnly;
  }

  openDialog(): void {
    this.validationErrors = {};
    this.parentAddressErrors = {};
    this.parents = this.buildParents(this.filledInfo?.relationshipInformation || []);
    this.showDialog = true;
  }

  closeDialog(): void {
    this.showDialog = false;
    this.validationErrors = {};
    this.parentAddressErrors = {};
  }

  save(): void {
    this.validationErrors = {};
    this.parentAddressErrors = {};
    const { fieldErrors, addressErrors } = validateParentGuardians(this.parents);
    this.validationErrors = fieldErrors;
    this.parentAddressErrors = addressErrors;
    if (Object.keys(this.validationErrors).length > 0) return;

    const payload = {
      participantId: this.selectedLead,
      programCode: this.programCode,
      relationshipInformation: mapParentGuardiansForSave(this.parents.getRawValue()),
    };
    this.referralDataService.saveRelationshipDetails(payload).subscribe({
      next: () => { this.awaitingSavedIds = true; this.leadUpdated.emit(true); this.closeDialog(); },
      error: () => { this.leadUpdated.emit(true); this.closeDialog(); },
    });
  }

  /**
   * The child removed persisted record(s) — inactivate them server-side straight away. Fired by the
   * delete icon and by ticking "Not Applicable"; brand-new rows (id 0) never reach here.
   */
  onDeleteSavedRecords(ids: number[]): void {
    if (!ids?.length || !this.selectedLead || !this.programCode) {
      return;
    }
    this.referralDataService
      .inactivateRelationshipDetails({ programCode: this.programCode, participantId: this.selectedLead, ids })
      .subscribe({
        next: () => this.leadUpdated.emit(true),
        error: () => { /* surfaced by the global error handler; the row stays removed in the form */ },
      });
  }

  private buildTableData(records: RelationshipInformation[]): void {
    this.tableData = (records || []).map((r) => ({
      ...r,
      fullName: [r.fname, r.mname, r.lname].filter(Boolean).join(' '),
      relationToParticipant: this.relationshipLabel(r.relationToParticipant),
      address: [
        [r.street1, r.street2].filter(Boolean).join(' '),
        r.city,
        [r.state, r.zipCode].filter(Boolean).join(' '),
      ].filter((p) => p && p.trim()).join(', '),
      status: r.status || '',
    }));
  }

  private relationshipLabel(code: any): string {
    const found = this.relationshipOptions?.find((o) => o.lkpValueCode === code);
    return found?.lkpValueName || code || '';
  }

  private buildParents(existing: RelationshipInformation[] = []): FormArray {
    const controls: FormGroup[] = [];
    if (!existing.length) {
      controls.push(buildParentGuardianRow(this.fb));
      controls.push(buildParentGuardianRow(this.fb, {}, true));
    } else {
      existing.forEach((rec, i) => {
        controls.push(buildParentGuardianRow(this.fb, { ...rec, dob: this.toDate(rec.dob) as any, notApplicable: i === 1 ? !parentRecordHasData(rec) : false }, i === 1));
      });
      if (controls.length === 1) {
        controls.push(buildParentGuardianRow(this.fb, {}, true));
      }
    }
    return this.fb.array(controls);
  }
}
